import { useEffect, useRef, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Lightning, WarningCircle, UploadSimple, Trash, Flame, Drop } from "@phosphor-icons/react";

const PROVIDER_META = {
  PGW:   { label: "PGW · Gas",     color: "bg-orange-600", icon: <Flame weight="fill" size={14}/> },
  PECO:  { label: "PECO · Electric", color: "bg-yellow-600", icon: <Lightning weight="fill" size={14}/> },
  PWD:   { label: "PWD · Water",   color: "bg-blue-600",   icon: <Drop weight="fill" size={14}/> },
  OTHER: { label: "Other",         color: "bg-slate-600",  icon: null },
};

export default function UtilityBillRadar() {
  const [bills, setBills] = useState([]);
  const [anomalyCount, setAnomalyCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/utility-bills");
      setBills(r.data.bills); setAnomalyCount(r.data.anomaly_count);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/utility-bills/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      if (r.data.anomaly) toast.error(`⚠ Anomaly: ${r.data.anomaly.message}`, { duration: 8000 });
      else toast.success(`Bill added · ${r.data.provider}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this bill?")) return;
    try { await api.delete(`/utility-bills/${id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <AdminShell>
      <Toaster position="top-right" richColors closeButton/>
      <div className="max-w-6xl mx-auto p-4 space-y-4">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-orange-600 flex items-center gap-1">
            <Flame weight="fill" size={12}/> Utility Bill Radar
          </div>
          <h1 className="font-display font-black text-3xl mt-1">Utility Bill Radar</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl">
            Drop PGW / PECO / PWD bills as photos or PDFs. Gemini OCR extracts usage + total, then flags any bill running <b>30%+ above the 6-bill rolling median</b> for that address. Catches hidden leaks weeks before tenants notice.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3 max-w-md">
          <div className="rugged-card p-3">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Bills</div>
            <div className="font-black text-2xl mt-1">{bills.length}</div>
          </div>
          <div className={`rugged-card p-3 ${anomalyCount > 0 ? "bg-red-50 border-red-500" : ""}`}>
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">🚨 Anomalies</div>
            <div className={`font-black text-2xl mt-1 ${anomalyCount > 0 ? "text-red-700" : ""}`}>{anomalyCount}</div>
          </div>
          <div className="rugged-card p-3">
            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Total $</div>
            <div className="font-black text-2xl mt-1">${(bills.reduce((s,b)=>s+(b.total_cents||0),0)/100).toFixed(0)}</div>
          </div>
        </div>

        <div>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={e => upload(e.target.files?.[0])} className="hidden" data-testid="bill-upload-input"/>
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="bill-upload-btn"
            className="h-11 px-4 bg-orange-600 hover:bg-orange-700 text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-2 shadow-[3px_3px_0_#0f172a] disabled:opacity-50">
            <UploadSimple weight="bold" size={14}/> {busy ? "OCR-ing…" : "Upload Utility Bill"}
          </button>
        </div>

        {bills.length === 0 ? (
          <div className="rugged-card p-8 text-center text-slate-500">
            No bills yet — upload one to start tracking anomalies.
          </div>
        ) : (
          <div className="rugged-card overflow-x-auto">
            <table className="w-full text-sm" data-testid="bills-table">
              <thead className="bg-slate-100">
                <tr className="text-left">
                  <th className="p-2">Provider</th>
                  <th className="p-2">Address</th>
                  <th className="p-2">Date</th>
                  <th className="p-2 text-right">Usage</th>
                  <th className="p-2 text-right">Total</th>
                  <th className="p-2">Status</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {bills.map(b => {
                  const meta = PROVIDER_META[b.provider] || PROVIDER_META.OTHER;
                  return (
                    <tr key={b.id} className="border-t border-slate-200 hover:bg-slate-50">
                      <td className="p-2">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-white text-[9px] font-black uppercase tracking-widest ${meta.color}`}>
                          {meta.icon} {meta.label}
                        </span>
                      </td>
                      <td className="p-2 max-w-[240px] truncate" title={b.service_address}>{b.service_address || "—"}</td>
                      <td className="p-2 font-mono text-xs">{b.statement_date || "—"}</td>
                      <td className="p-2 text-right font-mono">{b.usage_amount ? `${b.usage_amount} ${b.usage_unit}` : "—"}</td>
                      <td className="p-2 text-right font-mono">${(b.total_cents/100).toFixed(2)}</td>
                      <td className="p-2">
                        {b.anomaly ? (
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-white text-[9px] font-black uppercase tracking-widest ${b.anomaly.level === "high" ? "bg-red-700" : "bg-amber-500"}`}
                                title={b.anomaly.message} data-testid={`anomaly-${b.id}`}>
                            <WarningCircle weight="fill" size={10}/> +{b.anomaly.pct_over_median}%
                          </span>
                        ) : (
                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Normal</span>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <button onClick={() => del(b.id)} className="text-slate-400 hover:text-red-600" title="Delete">
                          <Trash size={14}/>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
